import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Sparkles,
  Video,
  PenSquare,
  ArrowRight,
  AlertTriangle,
  XOctagon,
  Users,
  Smile,
  Hourglass,
  Zap,
  Gift,
  Coffee,
  House,
  HandCoins,
} from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button, Textarea } from "@/components/ui";
import { toast } from "sonner";
import { ReviewServices } from "@/services/bedrock";
import { useComplianceChecker } from "@/utils";

interface ReviewOutreachScriptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  generatedScript: string;
  isGenerating: boolean;
  onContinueToRecording: (script: string) => void;
  fanId?: number;
}

const LOADING_PHRASES = [
  "Crafting a personalized review request...",
  "Polishing your outreach hooks...",
  "Analyzing your tone adjustments...",
  "Formatting for optimal teleprompter pacing...",
  "Reviewing compliance guidelines...",
  "Adding some referral magic...",
  "Ensuring your ask sounds natural...",
  "Perfecting your borrower thank-you...",
];

const REFINEMENT_SUGGESTIONS = [
  {
    label: "Make it warmer",
    prompt: "Rewrite this in an ultra-warm, friendly, and appreciative tone.",
    icon: <Smile className="size-3.5 text-pink-500 shrink-0" />,
  },
  {
    label: "Mention referral",
    prompt:
      "Acknowledge that referrals are the best compliment and politely ask them to keep you in mind for friends and family.",
    icon: <Users className="size-3.5 text-emerald-500 shrink-0 mt-0.5" />,
  },
  {
    label: "Emphasize closing",
    prompt: "Highlight my gratitude for their business and celebrate their successful transaction closing.",
    icon: <Zap className="size-3.5 text-amber-500 shrink-0 mt-0.5" />,
  },
  {
    label: "Keep it under 30s",
    prompt: "Make the script significantly shorter and punchier, easy to read in 30 seconds or less.",
    icon: <Hourglass className="size-3.5 text-blue-500 shrink-0 mt-0.5" />,
  },
  {
    label: "Invite feedback",
    prompt: "Make the tone focused on asking for their honest feedback to help you serve other homebuyers better.",
    icon: <Gift className="size-3.5 text-purple-500 shrink-0 mt-0.5" />,
  },
  {
    label: "Grab coffee",
    prompt: "Invite them to reach out anytime and offer to treat them to coffee next time they are in the area.",
    icon: <Coffee className="size-3.5 text-amber-600 shrink-0 mt-0.5" />,
  },
];

export function ReviewOutreachScriptDialog({
  open,
  onOpenChange,
  generatedScript,
  isGenerating,
  onContinueToRecording,
  fanId,
}: ReviewOutreachScriptDialogProps) {
  const [editedScript, setEditedScript] = useState(generatedScript);
  const [isRefining, setIsRefining] = useState(false);
  const [customInstructions, setCustomInstructions] = useState("");
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [transactionType, setTransactionType] = useState<"purchase" | "refinance" | null>(null);
  const [selectedSuggestions, setSelectedSuggestions] = useState<string[]>([]);

  const lastSyncedScript = useRef("");

  // Sync edited script when generated script arrives
  useEffect(() => {
    if (generatedScript && !isGenerating && generatedScript !== lastSyncedScript.current) {
      setEditedScript(generatedScript);
      lastSyncedScript.current = generatedScript;
    }
  }, [generatedScript, isGenerating]);

  // Handle rotating loading phrases
  const isLoadingAny = isGenerating || isRefining;

  useEffect(() => {
    if (!isLoadingAny) return;
    const interval = setInterval(() => {
      setPhraseIndex(prev => (prev + 1) % LOADING_PHRASES.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [isLoadingAny]);

  // Compliance checker
  const { findBlockedWords, findFlaggedWords } = useComplianceChecker();

  const blockedWords = useMemo(() => findBlockedWords(editedScript || ""), [editedScript, findBlockedWords]);
  const flaggedWords = useMemo(() => findFlaggedWords(editedScript || ""), [editedScript, findFlaggedWords]);

  const hasBlockedWords = blockedWords.length > 0;

  const handleToggleSuggestion = (label: string) => {
    setSelectedSuggestions(prev => (prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]));
  };

  // Staged AI refinement action
  const handleApplyRefinements = async () => {
    setIsRefining(true);
    try {
      const activePrompts = REFINEMENT_SUGGESTIONS.filter(s => selectedSuggestions.includes(s.label)).map(
        s => s.prompt
      );

      const combinedInstructions = [...activePrompts, customInstructions.trim()].filter(Boolean).join(" ");

      const result = await ReviewServices.generateReviewRequestScript({
        fanId,
        userInstructions: combinedInstructions || undefined,
        previousScript: editedScript,
        transactionType: transactionType || undefined,
        force: true,
      });

      if (result?.script) {
        setEditedScript(result.script);
        toast.success("Script refined successfully!");
        setCustomInstructions("");
        setSelectedSuggestions([]);
      } else {
        toast.error("Failed to refine script.");
      }
    } catch (error) {
      console.error("Refining script failed:", error);
      toast.error("An error occurred while refining the script.");
    } finally {
      setIsRefining(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="2xl">
      <Dialog.Header>
        <Dialog.Title className="flex items-center gap-2 text-xl font-extrabold text-slate-800">
          <Sparkles className="size-5 text-primary animate-pulse" />
          Review Your Outreach Script
        </Dialog.Title>
      </Dialog.Header>
      <Dialog.Content>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Left Column: Script Editor & Compliance */}
          <div className="md:col-span-6 flex flex-col gap-2">
            <label className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
              <PenSquare className="size-3.5" />
              Outreach Script
            </label>

            <div className="relative flex-1 flex flex-col">
              <Textarea
                value={editedScript}
                onChange={(e: any) => setEditedScript(e.target.value)}
                disabled={isLoadingAny}
                className="min-h-[280px] flex-1 text-sm leading-relaxed p-4 bg-white border-slate-300 hover:border-slate-400 focus:border-primary/50 focus:ring-1 focus:ring-primary rounded-lg shadow-sm resize-none font-medium text-slate-800 transition-all focus-visible:ring-0 focus-visible:ring-offset-0 outline-none"
                placeholder="Your customized review request script will appear here..."
              />
              {isLoadingAny && (
                <div className="absolute inset-0 bg-white/85 backdrop-blur-[2px] flex flex-col items-center justify-center rounded-lg border border-slate-200 z-20 transition-all duration-300">
                  <div className="relative flex items-center justify-center mb-4">
                    <div className="absolute size-14 rounded-full border-4 border-primary/10 animate-ping" />
                    <div className="size-12 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
                    <Sparkles className="absolute size-5 text-primary animate-pulse" />
                  </div>

                  <div className="text-center px-6 max-w-xs flex flex-col items-center gap-2">
                    <img alt={"SocialCoach AI"} src="https://cdn.socialcoach.io/socoai_mono_positive.svg" />
                    <p className="text-xs font-semibold text-slate-500 min-h-[16px] transition-all duration-500 ease-in-out">
                      {LOADING_PHRASES[phraseIndex]}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Compliance Alerts */}
            {hasBlockedWords && (
              <div className="mt-2 flex items-start gap-2.5 p-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-800 text-xs font-medium leading-relaxed shadow-sm">
                <XOctagon className="size-4 text-rose-600 shrink-0 mt-0.5 animate-bounce" />
                <div>
                  <span className="font-extrabold block uppercase tracking-wider text-rose-900 mb-0.5">
                    Blocked Content Detected
                  </span>
                  Your script contains compliance-blocked terms:{" "}
                  <span className="font-extrabold underline">{blockedWords.join(", ")}</span>. Please remove these terms
                  before continuing.
                </div>
              </div>
            )}

            {flaggedWords.length > 0 && !hasBlockedWords && (
              <div className="mt-2 flex items-start gap-2.5 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-xs font-medium leading-relaxed shadow-sm">
                <AlertTriangle className="size-4 text-amber-600 shrink-0 mt-0.5 animate-pulse" />
                <div>
                  <span className="font-extrabold block uppercase tracking-wider text-amber-900 mb-0.5">
                    Compliance Warning
                  </span>
                  Your script contains flagged keywords:{" "}
                  <span className="font-bold underline">{flaggedWords.join(", ")}</span>. You may want to revise them to
                  stay aligned with brand guidelines.
                </div>
              </div>
            )}
          </div>

          {/* Right Column: AI Refinement Options */}
          <div className="md:col-span-6 border-t md:border-t-0 md:border-l border-slate-100 pt-6 md:pt-0 md:pl-6 flex flex-col gap-4">
            <div className="flex items-center gap-1.5 text-base font-bold text-slate-950">
              Refine with
              <img
                alt={"SocialCoach AI"}
                src="https://cdn.socialcoach.io/socoai_mono_positive.svg"
                className="h-[13px]"
              />
            </div>

            {/* Transaction Type Button Group */}
            <div className="flex flex-col gap-2">
              <span className="text-xs font-semibold text-slate-500">Transaction Type:</span>
              <div className="flex gap-2.5 w-full">
                <button
                  type="button"
                  disabled={isLoadingAny}
                  onClick={() => setTransactionType(prev => (prev === "purchase" ? null : "purchase"))}
                  className={`flex-1 py-2 text-center text-xs font-bold border rounded-lg transition-all duration-200 select-none cursor-pointer flex items-center justify-center gap-1.5 ${
                    transactionType === "purchase"
                      ? "bg-primary border-primary text-white hover:bg-primary/95"
                      : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300"
                  }`}
                >
                  <House
                    className={`size-3.5 shrink-0 transition-colors ${
                      transactionType === "purchase" ? "text-white" : "text-emerald-500"
                    }`}
                  />
                  <span>Home Purchase</span>
                </button>
                <button
                  type="button"
                  disabled={isLoadingAny}
                  onClick={() => setTransactionType(prev => (prev === "refinance" ? null : "refinance"))}
                  className={`flex-1 py-2 text-center text-xs font-bold border rounded-lg transition-all duration-200 select-none cursor-pointer flex items-center justify-center gap-1.5 ${
                    transactionType === "refinance"
                      ? "bg-primary border-primary text-white hover:bg-primary/95"
                      : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300"
                  }`}
                >
                  <HandCoins
                    className={`size-3.5 shrink-0 transition-colors ${
                      transactionType === "refinance" ? "text-white" : "text-amber-500"
                    }`}
                  />
                  <span>Refinance</span>
                </button>
              </div>
            </div>

            {/* Predefined suggestions */}
            <div className="flex flex-col gap-2">
              <span className="text-xs font-semibold text-slate-500">Tone & Request Suggestions:</span>
              <div className="grid grid-cols-2 gap-2">
                {REFINEMENT_SUGGESTIONS.map(suggestion => {
                  const isSelected = selectedSuggestions.includes(suggestion.label);
                  return (
                    <button
                      key={suggestion.label}
                      type="button"
                      disabled={isLoadingAny}
                      onClick={() => handleToggleSuggestion(suggestion.label)}
                      className={`px-2 py-2 text-left text-xs font-bold border rounded-lg transition-all duration-200 flex items-start align-center gap-1.5 disabled:opacity-50 disabled:pointer-events-none leading-tight cursor-pointer ${
                        isSelected
                          ? "bg-indigo-50 border-primary/45 text-primary"
                          : "bg-slate-50 hover:bg-primary/5 hover:text-primary border-slate-200 hover:border-primary/25 text-slate-700"
                      }`}
                    >
                      {suggestion.icon}
                      <span>{suggestion.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Custom instructions & Refinement Button */}
            <div className="flex flex-col gap-3 mt-auto pt-2">
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-slate-500">Custom instructions (optional):</span>
                <input
                  type="text"
                  placeholder="Add a personal touch..."
                  value={customInstructions}
                  onChange={e => setCustomInstructions(e.target.value)}
                  disabled={isLoadingAny}
                  className="w-full text-xs px-3 py-2 h-9 border border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary rounded-lg outline-none font-medium placeholder:text-slate-400 disabled:opacity-50 transition-all bg-white"
                  onKeyDown={e => {
                    if (
                      e.key === "Enter" &&
                      (customInstructions.trim() || selectedSuggestions.length > 0 || transactionType)
                    ) {
                      handleApplyRefinements();
                    }
                  }}
                />
              </div>

              <Button
                type="button"
                variant={"outline"}
                disabled={
                  isLoadingAny || (!customInstructions.trim() && selectedSuggestions.length === 0 && !transactionType)
                }
                onClick={handleApplyRefinements}
                className="w-full h-10 flex items-center justify-center gap-1.5 border-primary border-2 text-primary hover:bg-primary/5"
              >
                <Sparkles className="size-4 animate-pulse" />
                Refine Script with AI
              </Button>
            </div>
          </div>
        </div>
      </Dialog.Content>
      <Dialog.Footer>
        <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-500 font-semibold">
          Cancel
        </Button>
        <Button
          onClick={() => onContinueToRecording(editedScript)}
          disabled={isLoadingAny || !editedScript.trim() || hasBlockedWords}
          className="font-bold flex items-center gap-2 h-10 px-6 bg-primary text-white border-none hover:bg-primary/90"
        >
          <Video className="size-4" />
          Continue to Teleprompter
          <ArrowRight className="size-4 ml-1" />
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
}
